import React from 'react'
import type { PublishFilters } from '../../types/publish'
import { DEFAULT_PUBLISH_FILTERS, normalizePublishFilters } from '../../types/publish'
import { usePostCategories } from '../../hooks/usePostCategories'
import CategorySelect from '../ui/CategorySelect'

type Props = {
  open: boolean
  onClose: () => void
  filters?: Partial<PublishFilters>
  onApply: (next: PublishFilters) => void
  onClear?: () => void
}

export default function PublishFilterDrawer({ open, onClose, filters, onApply, onClear }: Props) {
  const [local, setLocal] = React.useState<PublishFilters>(() => normalizePublishFilters(filters))
  const { items: categories } = usePostCategories({ enabled: open })
  const [locationHint, setLocationHint] = React.useState<string | null>(null)
  const radiusMiles = React.useMemo(() => {
    const km = Number(local.radius_km || 0)
    if (!Number.isFinite(km) || km <= 0) return 25
    return Math.max(1, Math.round(km / 1.60934))
  }, [local.radius_km])
  const distanceOptions = ['city', 5, 10, 25, 50, 100] as const
  React.useEffect(() => {
    setLocal(normalizePublishFilters(filters))
    setLocationHint(null)
  }, [filters])

  function apply() { onApply(normalizePublishFilters(local)) }
  function clear() {
    setLocal({ ...DEFAULT_PUBLISH_FILTERS })
    onClear?.()
  }
  function setDistance(distance: (typeof distanceOptions)[number]) {
    setLocal((p) => ({
      ...p,
      radius_km: distance === 'city' ? null : Math.max(1, Math.round(Number(distance) * 1.60934)),
    }))
  }
  function parseDistance(value: string): (typeof distanceOptions)[number] {
    if (value === 'city') return 'city'
    const n = Number(value)
    return n === 5 || n === 10 || n === 25 || n === 50 || n === 100 ? n : 25
  }
  function useLocation() {
    if (!navigator.geolocation) {
      setLocationHint('Location is unavailable in this browser.')
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocationHint(null)
        setLocal((p) => ({
          ...p,
          near_lat: Number(pos.coords.latitude),
          near_lng: Number(pos.coords.longitude),
          location_mode: 'near_me',
        }))
      },
      () => setLocationHint('Unable to read your location.'),
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
    )
  }
  return (
    <div aria-hidden={!open}>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity ${open ? 'opacity-100' : 'pointer-events-none opacity-0'} z-40`}
        onClick={onClose}
      />
      {/* Drawer */}
      <div
        role="dialog"
        aria-modal="true"
        className={`fixed right-0 top-0 h-full w-[360px] md:w-[420px] bg-neutral-900 border-l border-white/10 shadow-2xl z-50 transform transition-transform ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-neutral-900 border-b border-white/10 px-3 py-2">
          <div className="flex items-center gap-2">
            <div className="text-base font-extrabold">Filters</div>
            <button onClick={onClose} className="ml-auto text-white/80 hover:text-white">✕</button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {/* Mode */}
          {local.mode === 'saved' ? (
            <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-white/75">
              Saved Pixes are selected from the top app menu.
            </div>
          ) : (
            <div>
              <div className="text-sm text-white/70 mb-2">Mode</div>
              <div className="flex flex-wrap gap-2">
                {([
                  { key: 'all', label: 'Global' },
                  { key: 'my_subscriptions', label: 'My Subscriptions' },
                  { key: 'subscriptions', label: 'Paid Content' },
                ] as const).map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setLocal((p) => ({ ...p, mode: opt.key as any }))}
                    className={`px-3 py-1.5 rounded-full border text-xs ${
                      local.mode === (opt.key as any)
                        ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200'
                        : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="mt-2 text-xs text-white/60">
                My Subscriptions shows posts from creators you are subscribed to. Paid Content shows subscriber-only Pixes (paid content) across the platform.
              </div>
            </div>
          )}

          {/* Type */}
          <div>
            <div className="text-sm text-white/70 mb-2">Type</div>
            <div className="flex flex-wrap gap-2">
              {([
                { key: 'all', label: 'All' },
                { key: 'micro', label: 'Micro' },
                { key: 'zine', label: 'Zine' },
                { key: 'book', label: 'Book' },
                { key: 'event', label: 'Event' },
                { key: 'job', label: 'Job' },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setLocal((p) => ({ ...p, type: opt.key as any }))}
                  className={`px-3 py-1.5 rounded-full border text-xs ${
                    local.type === (opt.key as any)
                      ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200'
                      : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Location */}
          <div>
            <div className="text-sm text-white/70 mb-2">Location</div>
            <div className="flex flex-wrap gap-2">
              {([
                { key: 'anywhere', label: 'Anywhere' },
                { key: 'near_me', label: 'Near me' },
                { key: 'city', label: 'City' },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() =>
                    setLocal((p) => ({
                      ...p,
                      location_mode: opt.key,
                      near_lat: opt.key === 'near_me' ? p.near_lat : null,
                      near_lng: opt.key === 'near_me' ? p.near_lng : null,
                    }))
                  }
                  className={`px-3 py-1.5 rounded-full border text-xs ${
                    local.location_mode === opt.key
                      ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200'
                      : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {local.location_mode === 'near_me' ? (
              <div className="mt-3 space-y-2">
                <div className="mb-1 text-xs text-white/60">Radius</div>
                <div className="flex items-center gap-2">
                  <select
                    aria-label="Distance"
                    value={local.radius_km == null ? 'city' : String(radiusMiles)}
                    onChange={(e) => setDistance(parseDistance(e.target.value))}
                    className="h-8 min-w-[150px] rounded-md border border-white/10 bg-black px-2 text-sm text-white outline-none focus:border-white/20"
                  >
                    {distanceOptions.map((opt) => (
                      <option key={String(opt)} value={String(opt)}>
                        {opt === 'city' ? 'City' : `${opt} mi`}
                      </option>
                    ))}
                  </select>
                  {!(local.near_lat != null && local.near_lng != null) ? (
                    <button
                      type="button"
                      onClick={useLocation}
                      className="h-8 rounded-md border border-white/15 bg-white/5 px-2 text-xs text-white/90 hover:bg-white/10"
                    >
                      Use location
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {local.location_mode === 'city' ? (
              <div className="mt-3 grid grid-cols-1 gap-2">
                <input
                  value={local.city || ''}
                  onChange={(e) => setLocal((p) => ({ ...p, city: e.target.value }))}
                  placeholder="San Diego, CA"
                  className="w-full rounded-lg bg-black border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/20"
                />
                <select
                  aria-label="Distance"
                  value={local.radius_km == null ? 'city' : String(radiusMiles)}
                  onChange={(e) => setDistance(parseDistance(e.target.value))}
                  className="h-8 max-w-[170px] rounded-md border border-white/10 bg-black px-2 text-sm text-white outline-none focus:border-white/20"
                >
                  {distanceOptions.map((opt) => (
                    <option key={String(opt)} value={String(opt)}>
                      {opt === 'city' ? 'City' : `${opt} mi`}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {locationHint ? <div className="mt-2 text-xs text-white/65">{locationHint}</div> : null}

            <label className="mt-3 inline-flex items-center gap-2 text-sm text-white/85">
              <input
                type="checkbox"
                checked={Boolean(local.is_local)}
                onChange={(e) => setLocal((p) => ({ ...p, is_local: e.target.checked }))}
              />
              <span>Local only</span>
            </label>
          </div>

          {/* Category */}
          <div>
            <div className="text-sm text-white/70 mb-2">Category</div>
            <CategorySelect
              value={local.category || ''}
              onChange={(slug) => setLocal((p) => ({ ...p, category: slug }))}
              options={categories || []}
              placeholder="All categories"
            />
          </div>

          {/* Sort */}
          <div>
            <div className="text-sm text-white/70 mb-2">Sort</div>
            <div className="flex flex-wrap gap-2">
              {([
                { key: 'newest', label: 'Newest' },
                { key: 'popular', label: 'Popular' },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setLocal((p) => ({ ...p, sort: opt.key as any }))}
                  className={`px-3 py-1.5 rounded-full border text-xs ${
                    local.sort === (opt.key as any)
                      ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200'
                      : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-2 flex items-center justify-between">
            <button onClick={clear} className="text-sm text-white/70 hover:text-white">Clear</button>
            <button onClick={apply} className="px-3 py-2 rounded-lg bg-brand/80 hover:bg-brand text-sm font-semibold">Apply</button>
          </div>
        </div>
      </div>
    </div>
  )
}
